// path: src/utils/boardShuffle.js

export const TWISTED_ROTATIONS = [0, 90, 180, 270];
export const NON_ZERO_TWISTED_ROTATIONS = [90, 180, 270];

export function getCardOrderIds(cards = []) {
    return (cards || []).map((card) =>
        card == null ? null : String(card.id)
    );
}

export function isFullPositionChange(previousIds, nextIds) {
    if (!Array.isArray(previousIds) || !Array.isArray(nextIds)) {
        return false;
    }

    if (previousIds.length !== nextIds.length || previousIds.length <= 1) {
        return true;
    }

    for (let i = 0; i < previousIds.length; i += 1) {
        if (previousIds[i] === nextIds[i]) {
            return false;
        }
    }

    return true;
}

export function shuffleArrayCopy(array) {
    const shuffled = array.slice();

    for (let i = shuffled.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    return shuffled;
}

/**
 * Shuffle cards so no card remains in the same index as before.
 */
export function shuffleCardsChangingAllPositions(
    cards,
    { maxAttempts = 40, randomShuffle = shuffleArrayCopy } = {}
) {
    const source = Array.isArray(cards) ? cards.slice() : [];

    if (source.length <= 1) {
        return source;
    }

    const previousIds = getCardOrderIds(source);

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        const next = randomShuffle(source);

        if (isFullPositionChange(previousIds, getCardOrderIds(next))) {
            return next;
        }
    }

    // Deterministic fallback rotation when random retries fail.
    return source.map((_, index) => source[(index + 1) % source.length]);
}

export function getRandomTwistedRotation(
    { allowZero = true, random = Math.random } = {}
) {
    const pool = allowZero ? TWISTED_ROTATIONS : NON_ZERO_TWISTED_ROTATIONS;
    return pool[Math.floor(random() * pool.length)];
}

export function hasNonZeroTwistedRotation(cards = []) {
    return (cards || []).some((card) => Number(card?.twistedRotation) > 0);
}

function pickTwistedIndices(length, twistedCardLimit, random) {
    const indices = Array.from({ length }, (_, index) => index);

    for (let i = indices.length - 1; i > 0; i -= 1) {
        const j = Math.floor(random() * (i + 1));
        [indices[i], indices[j]] = [indices[j], indices[i]];
    }

    const limit = Number(twistedCardLimit) || 0;

    if (!limit || limit >= length) {
        return new Set(indices);
    }

    return new Set(indices.slice(0, limit));
}

/**
 * Apply twisted rotations. When requireVisibleTwist is true, guarantee at
 * least one card uses a non-zero rotation.
 */
export function applyTwistedRotations(
    cards,
    {
        twistedMode = false,
        twistedCardLimit = 0,
        requireVisibleTwist = false,
        random = Math.random,
    } = {}
) {
    const source = Array.isArray(cards) ? cards : [];

    if (!twistedMode) {
        return source.map((card) => ({
            ...card,
            twistedRotation: 0,
        }));
    }

    const twistedIndexSet = pickTwistedIndices(
        source.length,
        twistedCardLimit,
        random
    );

    let next = source.map((card, index) => ({
        ...card,
        twistedRotation: twistedIndexSet.has(index)
            ? getRandomTwistedRotation({
                  allowZero: true,
                  random,
              })
            : 0,
    }));

    if (requireVisibleTwist && next.length && !hasNonZeroTwistedRotation(next)) {
        const candidates = Array.from(twistedIndexSet);
        const forcedIndex =
            candidates[Math.floor(random() * candidates.length)] ?? 0;

        next = next.map((card, index) =>
            index === forcedIndex
                ? {
                      ...card,
                      twistedRotation: getRandomTwistedRotation({
                          allowZero: false,
                          random,
                      }),
                  }
                : card
        );
    }

    return next;
}
